import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LoaderCircle, Smartphone, BadgeCheck } from "lucide-react";
import { formatApiError } from "../../contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const headers = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("funz_token")}` },
  withCredentials: true,
});

export const PhonePanel = () => {
  const [data, setData] = useState(null);
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);

  const load = () => {
    axios.get(`${API}/phone/status`, headers()).then((r) => setData(r.data)).catch(() => setData(null));
  };
  useEffect(load, []);

  const send = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/phone/send`, { phone }, headers());
      toast.success("Code sent — check your phone.");
      setStep(2);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const verify = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/phone/verify`, { phone, code }, headers());
      toast.success("Phone verified ✅ Trust Score boosted.");
      load();
      setStep(1); setCode("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (!data) return null;
  return (
    <section className="rounded-3xl border border-white/10 bg-white/[0.04] p-6 backdrop-blur-xl" data-testid="phone-panel">
      <p className="flex items-center gap-2 font-display text-lg font-bold">
        <Smartphone className="h-5 w-5 text-[#06B6D4]" /> Mobile Verification
        {data.verified && <span className="flex items-center gap-1 rounded-full bg-[#22C55E]/10 px-3 py-1 font-jbmono text-[9px] font-bold uppercase tracking-wider text-[#22C55E]" data-testid="phone-verified"><BadgeCheck className="h-3 w-3" /> Verified</span>}
      </p>
      {!data.enabled ? (
        <p className="mt-3 rounded-2xl bg-[#F97316]/10 px-4 py-3 text-xs font-bold text-[#F97316]" data-testid="phone-coming-soon">
          SMS verification is being switched on — the platform is adding its Twilio Verify key. Check back shortly.
        </p>
      ) : data.verified ? (
        <p className="mt-3 text-xs text-slate-400">Your number <span className="font-bold text-white">{data.phone}</span> is verified and counts toward your Trust Score.</p>
      ) : (
        <div className="mt-4 space-y-3">
          {step === 1 ? (
            <div className="flex flex-wrap gap-2">
              <input data-testid="phone-number" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+919876543210"
                className="flex-1 rounded-2xl border border-white/10 bg-[#09090B]/80 px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none focus:border-[#7C3AED]/60" />
              <button data-testid="phone-send" onClick={send} disabled={busy || phone.trim().length < 8}
                className="rounded-full bg-gradient-to-r from-[#7C3AED] to-[#06B6D4] px-6 py-2.5 font-display text-xs font-bold uppercase tracking-wider text-white disabled:opacity-50">
                {busy ? <LoaderCircle className="h-3.5 w-3.5 animate-spin" /> : "Send code"}
              </button>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              <input data-testid="phone-code" value={code} onChange={(e) => setCode(e.target.value)} placeholder="6-digit code"
                className="flex-1 rounded-2xl border border-white/10 bg-[#09090B]/80 px-4 py-3 text-sm text-white placeholder:text-slate-600 outline-none focus:border-[#7C3AED]/60" />
              <button data-testid="phone-verify" onClick={verify} disabled={busy || code.trim().length < 4}
                className="rounded-full bg-gradient-to-r from-[#22C55E] to-[#06B6D4] px-6 py-2.5 font-display text-xs font-bold uppercase tracking-wider text-white disabled:opacity-50">
                {busy ? <LoaderCircle className="h-3.5 w-3.5 animate-spin" /> : "Verify"}
              </button>
            </div>
          )}
          <p className="font-jbmono text-[10px] uppercase tracking-widest text-slate-600">Use international format with country code</p>
        </div>
      )}
    </section>
  );
};
